if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Enhanced Zoom Modal functionality
if (!customElements.get('zoom-modal')) {
  customElements.define(
    'zoom-modal',
    class ZoomModal extends HTMLElement {
      constructor() {
        super();
        this.zoomLevel = 1;
        this.maxZoom = 3;
        this.minZoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        this.lastX = 0;
        this.lastY = 0;
        
        this.bindEvents();
      }

      bindEvents() {
        // Zoom controls
        this.addEventListener('click', this.handleZoomToggle.bind(this));
        this.addEventListener('wheel', this.handleWheel.bind(this));
        this.addEventListener('mousedown', this.handleMouseDown.bind(this));
        this.addEventListener('mousemove', this.handleMouseMove.bind(this));
        this.addEventListener('mouseup', this.handleMouseUp.bind(this));
        this.addEventListener('mouseleave', this.handleMouseUp.bind(this));
        
        // Touch events for mobile
        this.addEventListener('touchstart', this.handleTouchStart.bind(this));
        this.addEventListener('touchmove', this.handleTouchMove.bind(this));
        this.addEventListener('touchend', this.handleTouchEnd.bind(this));
        
        // Keyboard events
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
      }

      handleZoomToggle(event) {
        if (event.target.closest('.zoom-controls')) return;
        
        if (this.zoomLevel === this.minZoom) {
          this.zoomTo(2, event.clientX, event.clientY);
        } else {
          this.resetZoom();
        }
      }

      handleWheel(event) {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.2 : 0.2;
        const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));
        this.zoomTo(newZoom, event.clientX, event.clientY);
      }

      handleMouseDown(event) {
        if (this.zoomLevel > this.minZoom && !event.target.closest('.zoom-controls')) {
          this.isDragging = true;
          this.lastX = event.clientX;
          this.lastY = event.clientY;
          this.style.cursor = 'grabbing';
        }
      }

      handleMouseMove(event) {
        if (this.isDragging) {
          const deltaX = event.clientX - this.lastX;
          const deltaY = event.clientY - this.lastY;
          this.panX += deltaX;
          this.panY += deltaY;
          this.lastX = event.clientX;
          this.lastY = event.clientY;
          this.updateTransform();
        }
      }

      handleMouseUp() {
        this.isDragging = false;
        this.style.cursor = this.zoomLevel > this.minZoom ? 'grab' : 'zoom-in';
      }

      handleTouchStart(event) {
        if (event.touches.length === 1 && this.zoomLevel > this.minZoom) {
          this.isDragging = true;
          this.lastX = event.touches[0].clientX;
          this.lastY = event.touches[0].clientY;
        }
      }

      handleTouchMove(event) {
        if (this.isDragging && event.touches.length === 1) {
          event.preventDefault();
          const deltaX = event.touches[0].clientX - this.lastX;
          const deltaY = event.touches[0].clientY - this.lastY;
          this.panX += deltaX;
          this.panY += deltaY;
          this.lastX = event.touches[0].clientX;
          this.lastY = event.touches[0].clientY;
          this.updateTransform();
        }
      }

      handleTouchEnd() {
        this.isDragging = false;
      }

      handleKeyDown(event) {
        if (!this.closest('[open]')) return;
        
        switch(event.key) {
          case 'Escape':
            this.resetZoom();
            break;
          case '+':
          case '=':
            this.zoomIn();
            break;
          case '-':
            this.zoomOut();
            break;
          case '0':
            this.resetZoom();
            break;
        }
      }

      zoomTo(level, clientX = null, clientY = null) {
        const oldZoom = this.zoomLevel;
        this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, level));
        
        if (clientX !== null && clientY !== null) {
          const rect = this.getBoundingClientRect();
          const x = clientX - rect.left - rect.width / 2;
          const y = clientY - rect.top - rect.height / 2;
          
          const zoomRatio = this.zoomLevel / oldZoom;
          this.panX = (this.panX - x) * zoomRatio + x;
          this.panY = (this.panY - y) * zoomRatio + y;
        }
        
        this.updateTransform();
        this.updateCursor();
        this.updateZoomControls();
      }

      zoomIn() {
        this.zoomTo(this.zoomLevel + 0.5);
      }

      zoomOut() {
        this.zoomTo(this.zoomLevel - 0.5);
      }

      resetZoom() {
        this.zoomLevel = this.minZoom;
        this.panX = 0;
        this.panY = 0;
        this.updateTransform();
        this.updateCursor();
        this.updateZoomControls();
      }

      updateTransform() {
        const image = this.querySelector('img');
        if (image) {
          image.style.transform = `scale(${this.zoomLevel}) translate(${this.panX / this.zoomLevel}px, ${this.panY / this.zoomLevel}px)`;
        }
      }

      updateCursor() {
        this.style.cursor = this.zoomLevel > this.minZoom ? 'grab' : 'zoom-in';
      }

      updateZoomControls() {
        const zoomInBtn = this.querySelector('.zoom-in');
        const zoomOutBtn = this.querySelector('.zoom-out');
        const resetBtn = this.querySelector('.zoom-reset');
        
        if (zoomInBtn) zoomInBtn.disabled = this.zoomLevel >= this.maxZoom;
        if (zoomOutBtn) zoomOutBtn.disabled = this.zoomLevel <= this.minZoom;
        if (resetBtn) resetBtn.style.display = this.zoomLevel > this.minZoom ? 'block' : 'none';
      }
    }
  );
}
